import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  CaretLeft, 
  CaretRight, 
  DownloadSimple, 
  CurrencyDollar,
  Users,
  CalendarX,
  Umbrella,
  Lock,
  Warning,
  FileText
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const RECORD_TYPES = {
  ANTICIPO: { label: 'Anticipo', color: 'bg-emerald-100 text-emerald-800', shortLabel: '$' },
  TRABAJO: { label: 'Trabajó', color: 'bg-blue-100 text-blue-800', shortLabel: 'T' },
  INASISTENCIA: { label: 'Inasistencia', color: 'bg-red-100 text-red-800', shortLabel: 'I' },
  VACACIONES: { label: 'Vacaciones', color: 'bg-amber-100 text-amber-800', shortLabel: 'V' },
  LICENCIA: { label: 'Licencia', color: 'bg-purple-100 text-purple-800', shortLabel: 'L' },
  PERMISO: { label: 'Permiso', color: 'bg-orange-100 text-orange-800', shortLabel: 'P' },
  FERIADO: { label: 'Feriado', color: 'bg-zinc-200 text-zinc-700', shortLabel: 'F' },
  DESCANSO: { label: 'Descanso', color: 'bg-cyan-100 text-cyan-700', shortLabel: 'D' },
  NO_EXISTENTE: { label: 'No contratado', color: 'bg-zinc-100 text-zinc-400', shortLabel: 'Z' },
};

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const STATUS_LABELS = {
  abierto: { label: 'Abierto', color: 'bg-emerald-100 text-emerald-800' },
  en_cierre: { label: 'En Cierre', color: 'bg-amber-100 text-amber-800' },
  cerrado: { label: 'Cerrado', color: 'bg-red-100 text-red-800' },
};

const MonthlyGridPage = () => {
  const { axiosInstance, isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gridData, setGridData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  
  // Operative month state
  const [operativeMonth, setOperativeMonth] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  
  // Current month/year for display
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
  
  // Cell edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editRecordType, setEditRecordType] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editObservation, setEditObservation] = useState('');
  
  const scrollContainerRef = useRef(null);
  const [allowEditPastDays, setAllowEditPastDays] = useState(true);
  const [editableCutoffDate, setEditableCutoffDate] = useState(null);

  // Load operative month first
  const loadOperativeMonth = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/operative-month');
      setOperativeMonth(response.data);
      
      // For non-admin users, force the operative month
      if (!isAdmin) {
        setCurrentYear(response.data.year);
        setCurrentMonth(response.data.month);
        // Can only edit if status is 'abierto' or 'en_cierre'
        setCanEdit(response.data.status !== 'cerrado');
      } else {
        // Admin can always edit
        setCanEdit(true);
      }
      
      return response.data;
    } catch (error) {
      // error handled silently
      return null;
    }
  }, [axiosInstance, isAdmin]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedBranch) params.branch_id = selectedBranch;
      
      const results = await Promise.allSettled([
        axiosInstance.get(`/api/daily-records/month/${currentYear}/${currentMonth}`, { params }),
        isAdmin ? axiosInstance.get('/api/branches') : Promise.resolve({ data: [] }),
        axiosInstance.get('/api/settings'),
      ]);
      
      const [gridResult, branchesResult, settingsResult] = results;
      
      if (gridResult.status === 'fulfilled') {
        setGridData(gridResult.value.data);
      } else {
        console.error('Error cargando planilla:', gridResult.reason);
        setGridData(null);
      }
      
      if (branchesResult.status === 'fulfilled') {
        setBranches(branchesResult.value.data);
      }
      
      if (settingsResult.status === 'fulfilled') {
        setAllowEditPastDays(settingsResult.value.data?.allow_edit_past_days ?? true);
        setEditableCutoffDate(settingsResult.value.data?.editable_cutoff_date || null);
      }
      
      // Check if current view matches operative month for editing
      if (operativeMonth && !isAdmin) {
        const isOperativeMonth = currentYear === operativeMonth.year && currentMonth === operativeMonth.month;
        setCanEdit(isOperativeMonth && operativeMonth.status !== 'cerrado');
      }
    } catch (error) {
      console.error('Error inesperado en loadData:', error);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, isAdmin, currentYear, currentMonth, selectedBranch, operativeMonth]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      const opMonth = await loadOperativeMonth();
      if (opMonth && !isAdmin) {
        // Non-admin users see only operative month
        setCurrentYear(opMonth.year);
        setCurrentMonth(opMonth.month);
      }
    };
    init();
  }, [loadOperativeMonth, isAdmin]);

  useEffect(() => {
    if (operativeMonth) {
      loadData();
    }
  }, [loadData, operativeMonth, currentYear, currentMonth]);

  // Scroll to today's column
  const scrollToToday = useCallback(() => {
    if (scrollContainerRef.current) {
      const todayDate = new Date();
      const todayMonth = todayDate.getMonth() + 1;
      const todayYear = todayDate.getFullYear();
      
      if (currentMonth === todayMonth && currentYear === todayYear) {
        const todayDay = todayDate.getDate();
        const containerWidth = scrollContainerRef.current.clientWidth;
        const fixedColumnsWidth = 336;
        const dayColumnWidth = 56;
        const todayColumnStart = fixedColumnsWidth + (todayDay - 1) * dayColumnWidth;
        const scrollPosition = todayColumnStart - (containerWidth / 2) + (dayColumnWidth / 2);
        scrollContainerRef.current.scrollLeft = Math.max(0, scrollPosition);
      } else {
        scrollContainerRef.current.scrollLeft = 0;
      }
    }
  }, [currentMonth, currentYear]);

  useEffect(() => {
    if (gridData) {
      setTimeout(scrollToToday, 100);
    }
  }, [gridData, scrollToToday]);

  const handlePrevMonth = () => {
    if (!isAdmin) return; // Non-admin cannot navigate
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (!isAdmin) return; // Non-admin cannot navigate
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleCellClick = (worker, dateStr, currentData) => {
    // Block Z cells (not hired yet)
    if (currentData?.record_type === 'NO_EXISTENTE' || currentData?.blocked) {
      return;
    }
    
    // Check if editing is allowed
    if (!canEdit) {
      alert('No puede editar registros en este momento. El mes operativo está cerrado o está viendo un mes diferente al operativo.');
      return;
    }
    
    // For non-admin, check if the date is within operative month
    if (!isAdmin && operativeMonth) {
      const recordYear = parseInt(dateStr.substring(0, 4));
      const recordMonth = parseInt(dateStr.substring(5, 7));
      if (recordYear !== operativeMonth.year || recordMonth !== operativeMonth.month) {
        alert('Solo puede editar registros del mes operativo vigente.');
        return;
      }
    }

    // Block past days for non-admin when setting is disabled (6AM cutoff rule)
    if (!isAdmin && !allowEditPastDays && editableCutoffDate) {
      if (dateStr < editableCutoffDate) {
        alert('El plazo para ingresar este dia ya vencio (limite: 06:00 AM del dia siguiente).');
        return;
      }
    }
    
    setEditingCell({ worker, dateStr, currentData });
    setEditRecordType(currentData?.record_type || '');
    setEditAmount(currentData?.amount || '');
    setEditObservation(currentData?.observation || '');
    setShowEditModal(true);
  };

  const handleSaveCell = async () => {
    if (!editingCell || !editRecordType) return;
    
    setSaving(true);
    try {
      const payload = {
        worker_id: editingCell.worker.id,
        date: editingCell.dateStr,
        record_type: editRecordType,
        amount: editRecordType === 'ANTICIPO' ? parseFloat(editAmount) : null,
        observation: editObservation || null,
      };
      
      await axiosInstance.post('/api/daily-records', payload);
      setShowEditModal(false);
      await loadData();
      setTimeout(scrollToToday, 150);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCell = async () => {
    if (!editingCell) return;
    
    if (!window.confirm('¿Eliminar este registro?')) return;
    
    setSaving(true);
    try {
      await axiosInstance.delete(`/api/daily-records/${editingCell.worker.id}/${editingCell.dateStr}`);
      setShowEditModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      params.append('year', currentYear);
      params.append('month', currentMonth);
      params.append('format', 'xlsx');
      if (selectedBranch) params.append('branch_id', selectedBranch);
      
      const response = await axiosInstance.get(`/api/export/monthly-grid?${params.toString()}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `planilla_mensual_${currentYear}_${String(currentMonth).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al exportar');
    }
  };

  const handleExportMissingDays = async () => {
    try {
      const params = new URLSearchParams();
      params.append('year', currentYear);
      params.append('month', currentMonth);
      if (selectedBranch) params.append('branch_id', selectedBranch);

      const response = await axiosInstance.get(`/api/export/missing-days?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/plain;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dias_pendientes_${currentYear}_${String(currentMonth).padStart(2, '0')}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al exportar reporte de pendientes');
    }
  };

  const getDayOfWeek = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    return date.getDay();
  };

  const isWeekend = (year, month, day) => {
    const dow = getDayOfWeek(year, month, day);
    return dow === 0 || dow === 6;
  };

  const isToday = (dateStr) => {
    return dateStr === today.toISOString().split('T')[0];
  };

  const renderCellContent = (dayData) => {
    if (!dayData) return null;
    
    // Z cell - not hired yet
    if (dayData.record_type === 'NO_EXISTENTE' || dayData.blocked) {
      return (
        <span className="text-xs font-bold text-zinc-300" title="Trabajador aún no ingresaba a la empresa">
          Z
        </span>
      );
    }
    
    const typeInfo = RECORD_TYPES[dayData.record_type];
    if (!typeInfo) return null;
    
    if (dayData.record_type === 'ANTICIPO' && dayData.amount) {
      return (
        <span className="font-mono text-xs font-medium text-emerald-700">
          {Math.round(dayData.amount / 1000)}k
        </span>
      );
    }
    
    return (
      <span className={`text-xs font-bold ${typeInfo.color.split(' ')[1]}`}>
        {typeInfo.shortLabel}
      </span>
    );
  };

  const isPastDay = (dateStr) => {
    if (isAdmin || allowEditPastDays) return false;
    if (!editableCutoffDate) return false;
    return dateStr < editableCutoffDate;
  };

  const getCellBackground = (dayData, weekend, todayCell, pastLocked) => {
    // Z cells - blocked, not hired yet
    if (dayData?.record_type === 'NO_EXISTENTE' || dayData?.blocked) {
      return 'bg-zinc-100 cursor-not-allowed';
    }

    // Past day locked for non-admin
    if (pastLocked) {
      let base = 'cursor-not-allowed opacity-60';
      if (weekend) base += ' bg-zinc-100';
      else base += ' bg-zinc-50';
      if (dayData) {
        const typeInfo = RECORD_TYPES[dayData.record_type];
        if (typeInfo) base += ` ${typeInfo.color.split(' ')[0]}`;
      }
      return base;
    }
    
    let base = 'transition-colors';
    
    if (canEdit) {
      base += ' cursor-pointer hover:bg-zinc-100';
    } else {
      base += ' cursor-not-allowed';
    }
    
    if (todayCell) {
      base += ' ring-2 ring-zinc-900 ring-inset';
    }
    
    if (weekend) {
      base += ' bg-zinc-50';
    }
    
    if (dayData) {
      const typeInfo = RECORD_TYPES[dayData.record_type];
      if (typeInfo) {
        base += ` ${typeInfo.color.split(' ')[0]}`;
      }
    }
    
    return base;
  };

  // Check if viewing operative month
  const isViewingOperativeMonth = operativeMonth && 
    currentYear === operativeMonth.year && 
    currentMonth === operativeMonth.month;

  if (loading && !gridData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const summary = gridData?.summary || {};
  const daysInMonth = gridData?.days_in_month || 30;

  return (
    <div className="space-y-4" data-testid="monthly-grid-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="grid-title">
            {isAdmin ? 'Planilla Mensual' : 'Mes Operativo'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {isAdmin ? 'Control diario de asistencia y anticipos' : 'Registro diario de su sucursal'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && branches.length > 0 && (
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="form-select w-48"
              data-testid="branch-filter"
            >
              <option value="">Todas las sucursales</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={handleExport}
            className="btn-secondary flex items-center gap-2"
            data-testid="export-grid-btn"
          >
            <DownloadSimple size={18} />
            Exportar
          </button>
          {isAdmin && (
            <button
              onClick={handleExportMissingDays}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors"
              data-testid="export-missing-days-btn"
            >
              <FileText size={18} />
              Pendientes
            </button>
          )}
        </div>
      </div>

      {/* Operative Month Status Banner (for non-admins) */}
      {!isAdmin && operativeMonth && (
        <div className={`p-4 border ${operativeMonth.status === 'cerrado' ? 'border-red-200 bg-red-50' : operativeMonth.status === 'en_cierre' ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {operativeMonth.status === 'cerrado' ? (
                <Lock size={20} className="text-red-600" />
              ) : operativeMonth.status === 'en_cierre' ? (
                <Warning size={20} className="text-amber-600" />
              ) : null}
              <div>
                <p className="font-medium text-zinc-900">
                  Mes Operativo: {MONTHS[operativeMonth.month - 1]} {operativeMonth.year}
                </p>
                <p className="text-sm text-zinc-600">
                  Estado: <span className={`px-2 py-0.5 text-xs ${STATUS_LABELS[operativeMonth.status]?.color}`}>
                    {STATUS_LABELS[operativeMonth.status]?.label}
                  </span>
                  {operativeMonth.status === 'cerrado' && ' - Solo lectura'}
                  {operativeMonth.status === 'en_cierre' && ' - Complete los registros pendientes'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <CurrencyDollar size={16} className="text-emerald-600" />
            <span className="kpi-label">Anticipos Hoy</span>
          </div>
          <div className="kpi-value text-xl">{formatCLP(summary.today?.advances_total || 0)}</div>
          <p className="text-xs text-zinc-500">{summary.today?.advances_count || 0} anticipos</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <CurrencyDollar size={16} className="text-zinc-600" />
            <span className="kpi-label">Anticipos Semana</span>
          </div>
          <div className="kpi-value text-xl">{formatCLP(summary.week?.advances_total || 0)}</div>
          <p className="text-xs text-zinc-500">{summary.week?.advances_count || 0} anticipos</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <CurrencyDollar size={16} className="text-zinc-600" />
            <span className="kpi-label">Anticipos Mes</span>
          </div>
          <div className="kpi-value text-xl">{formatCLP(summary.month?.advances_total || 0)}</div>
          <p className="text-xs text-zinc-500">{summary.month?.advances_count || 0} anticipos</p>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <Users size={16} className="text-blue-600" />
            <span className="kpi-label">Trabajando Hoy</span>
          </div>
          <div className="kpi-value text-xl">{summary.today?.trabajando || 0}</div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <CalendarX size={16} className="text-red-600" />
            <span className="kpi-label">Inasistencias Hoy</span>
          </div>
          <div className="kpi-value text-xl">{summary.today?.inasistencias || 0}</div>
        </div>
        <div className="kpi-card">
          <div className="flex items-center gap-2 mb-1">
            <Umbrella size={16} className="text-amber-600" />
            <span className="kpi-label">Vacaciones Hoy</span>
          </div>
          <div className="kpi-value text-xl">{summary.today?.vacaciones || 0}</div>
        </div>
      </div>

      {/* Month Navigation - Only for Admins */}
      <div className="flex items-center justify-center gap-4 py-2">
        {isAdmin ? (
          <>
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-zinc-100 transition-colors"
              data-testid="prev-month-btn"
            >
              <CaretLeft size={24} />
            </button>
            <h2 className="font-heading text-xl font-semibold text-zinc-900 min-w-[200px] text-center">
              {MONTHS[currentMonth - 1]} {currentYear}
              {isViewingOperativeMonth && (
                <span className={`ml-2 text-xs px-2 py-0.5 ${STATUS_LABELS[operativeMonth?.status]?.color}`}>
                  {STATUS_LABELS[operativeMonth?.status]?.label}
                </span>
              )}
            </h2>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-zinc-100 transition-colors"
              data-testid="next-month-btn"
            >
              <CaretRight size={24} />
            </button>
            <button
              onClick={() => {
                if (operativeMonth) {
                  setCurrentMonth(operativeMonth.month);
                  setCurrentYear(operativeMonth.year);
                  setTimeout(scrollToToday, 200);
                }
              }}
              className="ml-4 px-3 py-1 text-sm bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
              data-testid="go-to-operative-btn"
            >
              Mes Operativo
            </button>
            <button
              onClick={() => {
                const now = new Date();
                setCurrentMonth(now.getMonth() + 1);
                setCurrentYear(now.getFullYear());
                setTimeout(scrollToToday, 200);
              }}
              className="px-3 py-1 text-sm border border-zinc-300 hover:bg-zinc-100 transition-colors"
              data-testid="go-to-today-btn"
            >
              Hoy
            </button>
          </>
        ) : (
          <h2 className="font-heading text-xl font-semibold text-zinc-900">
            {MONTHS[currentMonth - 1]} {currentYear}
          </h2>
        )}
      </div>

      {/* Warning if admin is viewing different month than operative */}
      {isAdmin && !isViewingOperativeMonth && operativeMonth && (
        <div className="p-3 border border-amber-200 bg-amber-50 text-sm text-amber-800 flex items-center gap-2">
          <Warning size={18} />
          <span>
            Está viendo un mes diferente al operativo actual ({MONTHS[operativeMonth.month - 1]} {operativeMonth.year}).
            Los usuarios de sucursal solo pueden ver y editar el mes operativo.
          </span>
        </div>
      )}

      {/* Grid Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(RECORD_TYPES).map(([key, value]) => (
          <div key={key} className="flex items-center gap-1">
            <span className={`w-6 h-6 flex items-center justify-center ${value.color}`}>
              {value.shortLabel}
            </span>
            <span className="text-zinc-600">{value.label}</span>
          </div>
        ))}
        {!canEdit && (
          <div className="flex items-center gap-1 ml-4 text-red-600">
            <Lock size={14} />
            <span>Solo lectura</span>
          </div>
        )}
        {canEdit && !isAdmin && !allowEditPastDays && (
          <div className="flex items-center gap-1 ml-4 text-amber-600">
            <Lock size={14} />
            <span>Dias anteriores bloqueados (limite: 06:00 AM del dia siguiente)</span>
          </div>
        )}
      </div>

      {/* Grid Table */}
      <div className="border border-zinc-200 bg-white">
        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto"
          style={{ maxHeight: 'calc(100vh - 480px)' }}
        >
          <table className="w-full border-collapse" data-testid="monthly-grid-table">
            <thead className="sticky top-0 z-10 bg-zinc-50">
              <tr>
                <th className="sticky left-0 z-20 bg-zinc-50 border-b border-r border-zinc-200 px-2 py-2 text-left text-xs uppercase tracking-wider font-medium text-zinc-500 w-16 min-w-[64px]">
                  Código
                </th>
                <th className="sticky left-16 z-20 bg-zinc-50 border-b border-r border-zinc-200 px-2 py-2 text-left text-xs uppercase tracking-wider font-medium text-zinc-500 w-48 min-w-[192px]">
                  Nombre
                </th>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const weekend = isWeekend(currentYear, currentMonth, day);
                  const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const todayCell = isToday(dateStr);
                  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
                  const dow = getDayOfWeek(currentYear, currentMonth, day);
                  
                  return (
                    <th 
                      key={day}
                      className={`border-b border-r border-zinc-200 px-1 py-1 text-center text-xs font-medium w-14 min-w-[56px] ${weekend ? 'bg-zinc-100 text-zinc-400' : 'text-zinc-500'} ${todayCell ? 'bg-zinc-900 text-white' : ''}`}
                    >
                      <div className="font-bold">{day}</div>
                      <div className="text-[10px]">{dayNames[dow]}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {gridData?.workers?.map((worker) => {
                const isInactive = worker.status !== 'activo';
                
                return (
                  <tr 
                    key={worker.id}
                    className={`${isInactive ? 'bg-red-50/50' : ''}`}
                  >
                    <td className={`sticky left-0 z-10 ${isInactive ? 'bg-red-50' : 'bg-white'} border-b border-r border-zinc-200 px-2 py-1 text-xs font-mono text-zinc-600 w-16 min-w-[64px]`}>
                      {worker.code}
                    </td>
                    <td className={`sticky left-16 z-10 ${isInactive ? 'bg-red-50' : 'bg-white'} border-b border-r border-zinc-200 px-2 py-1 w-48 min-w-[192px]`}>
                      <div className="text-sm font-medium text-zinc-900 leading-tight">
                        {worker.first_name} {worker.last_name}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`text-[10px] px-1 py-0.5 ${
                          worker.status === 'activo' ? 'badge-activo' :
                          worker.status === 'vacaciones' ? 'badge-vacaciones' :
                          worker.status === 'con licencia' ? 'badge-licencia' :
                          worker.status === 'suspendido' ? 'badge-suspendido' :
                          'badge-desvinculado'
                        }`}>
                          {worker.status}
                        </span>
                        {worker.cargo && worker.cargo !== 'Sin cargo' && (
                          <span className="text-[10px] text-zinc-400">{worker.cargo}</span>
                        )}
                      </div>
                    </td>
                    {Array.from({ length: daysInMonth }, (_, i) => {
                      const day = i + 1;
                      const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const dayData = worker.days[dateStr];
                      const weekend = isWeekend(currentYear, currentMonth, day);
                      const todayCell = isToday(dateStr);
                      const pastLocked = isPastDay(dateStr);
                      
                      return (
                        <td 
                          key={dateStr}
                          onClick={() => handleCellClick(worker, dateStr, dayData)}
                          className={`border-b border-r border-zinc-200 px-1 py-1 text-center w-14 min-w-[56px] h-10 ${getCellBackground(dayData, weekend, todayCell, pastLocked)}`}
                          title={pastLocked ? 'La edicion de dias anteriores esta desactivada por administracion' : undefined}
                          data-testid={`cell-${worker.id}-${dateStr}`}
                        >
                          {renderCellContent(dayData)}
                          {pastLocked && canEdit && (
                            <Lock size={8} className="text-zinc-400 mx-auto -mt-0.5" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Cell Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCell?.worker?.first_name} {editingCell?.worker?.last_name}
            </DialogTitle>
            <p className="text-sm text-zinc-500">
              {editingCell?.dateStr}
            </p>
          </DialogHeader>
          <div className="space-y-4">
            {/* Record Type Selection */}
            <div>
              <label className="form-label">Tipo de Registro</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(RECORD_TYPES).filter(([key]) => key !== 'NO_EXISTENTE').map(([key, value]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setEditRecordType(key)}
                    className={`p-3 border text-left text-sm transition-colors ${
                      editRecordType === key
                        ? 'border-zinc-900 bg-zinc-50'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid={`record-type-${key}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 flex items-center justify-center text-xs font-bold ${value.color}`}>
                        {value.shortLabel}
                      </span>
                      <span>{value.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Amount (only for ANTICIPO) */}
            {editRecordType === 'ANTICIPO' && (
              <div>
                <label className="form-label">Monto (CLP)</label>
                <input
                  type="number"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  className="form-input font-mono"
                  placeholder="0"
                  min="1"
                  data-testid="edit-amount-input"
                />
              </div>
            )}

            {/* Observation */}
            <div>
              <label className="form-label">Observación (opcional)</label>
              <textarea
                value={editObservation}
                onChange={(e) => setEditObservation(e.target.value)}
                rows={2}
                className="form-input resize-none"
                placeholder="Agregar nota..."
                data-testid="edit-observation-input"
              />
            </div>

            <DialogFooter className="flex justify-between">
              <div>
                {editingCell?.currentData && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteCell}
                    disabled={saving}
                    className="rounded-none"
                    data-testid="delete-record-btn"
                  >
                    Eliminar
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="rounded-none"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveCell}
                  disabled={saving || !editRecordType || (editRecordType === 'ANTICIPO' && !editAmount)}
                  className="rounded-none bg-zinc-900"
                  data-testid="save-record-btn"
                >
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyGridPage;
